import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coins, X, Loader2, Bitcoin, Zap, QrCode, History } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { BTC_ENABLED } from "@/lib/flags";

const MIN_CREDITS = 5;
const PACKS = [5, 10, 25, 50, 100];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export function RechargeModal() {
  const { user, refreshUser, fmtMoney } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("Charge with credits to continue");
  const [amount, setAmount] = useState(MIN_CREDITS);
  const [busy, setBusy] = useState(false);
  const [autoOn, setAutoOn] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    api.get("/credits/config").then((r) => setCfg(r.data)).catch(() => setCfg({ razorpay_enabled: false }));
  }, []);

  useEffect(() => {
    const onOpen = (e) => {
      setMessage(e?.detail?.message || "Charge with credits to continue");
      setAmount(MIN_CREDITS);
      setAutoOn(!!user?.auto_topup_enabled);
      setOpen(true);
    };
    window.addEventListener("open-recharge", onOpen);
    return () => window.removeEventListener("open-recharge", onOpen);
  }, [user?.auto_topup_enabled]);

  useEffect(() => { setAutoOn(!!user?.auto_topup_enabled); }, [user?.auto_topup_enabled]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy]);

  const toggleAuto = async () => {
    const next = !autoOn;
    setAutoBusy(true);
    try {
      await api.post("/me/auto-topup", { enabled: next, amount: Math.max(MIN_CREDITS, Math.floor(amount || 0)) });
      setAutoOn(next);
      await refreshUser?.();
      toast.success(next ? `Auto-recharge on — ${Math.max(MIN_CREDITS, Math.floor(amount || 0))} credits added automatically when you run low.` : "Auto-recharge turned off.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAutoBusy(false);
    }
  };

  const buy = async () => {
    const amt = Math.max(MIN_CREDITS, Math.floor(amount || 0));
    if (!cfg?.razorpay_enabled) {
      toast.error("Payments aren't available right now. Please try again later.");
      return;
    }
    setBusy(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load the payment window. Check your connection.");
      const { data: order } = await api.post("/credits/order", { credits: amt });
      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "My Kind",
          description: `${order.credits} credits`,
          image: "/logo192.png",
          prefill: { name: order.name, email: order.email, contact: order.contact },
          theme: { color: "#E23744" },
          handler: async (res) => {
            try {
              const { data } = await api.post("/credits/verify", {
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              await refreshUser?.();
              toast.success(`Payment successful — ${data.purchased} credits added!`);
              setOpen(false);
              resolve();
            } catch (e) {
              toast.error(apiError(e.response?.data?.detail) || "We couldn't confirm your payment. If money was debited, it'll be sorted shortly.");
              reject(e);
            }
          },
          modal: { ondismiss: () => { toast.message("Payment cancelled."); resolve(); } },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp?.error?.description || "Payment failed. Please try again.");
          reject(new Error("payment_failed"));
        });
        rzp.open();
      });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message || "Couldn't start payment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] grid place-items-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          data-testid="recharge-modal"
          onClick={() => !busy && setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-rud glass p-6 relative"
          >
            <button data-testid="recharge-close" onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-sec hover:text-[var(--rud-text)] transition-colors">
              <X size={18} />
            </button>

            <div className="w-11 h-11 rounded-full bg-brand/15 grid place-items-center text-brand mb-4">
              <Zap size={22} />
            </div>
            <h3 className="font-serif text-2xl tracking-tight" data-testid="recharge-title">{message}</h3>
            <p className="text-sec text-sm mt-2">
              You've used your free trial. Top up to keep searching, saving &amp; contributing. <b className="text-[var(--rud-text)]">1 credit = {cfg?.razorpay_enabled ? `₹${Math.round(cfg.inr_per_credit || 83.2)}` : fmtMoney(1)}</b> · minimum {MIN_CREDITS} credits.
            </p>

            <div className="grid grid-cols-5 gap-2 mt-5" data-testid="recharge-packs">
              {PACKS.map((p) => (
                <button key={p} data-testid={`recharge-pack-${p}`} onClick={() => setAmount(p)}
                  className={`rounded-xl border py-2.5 text-sm font-medium transition-colors ${amount === p ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand hover:text-[var(--rud-text)]"}`}>
                  {p}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-3">
              <div className="flex-1 flex items-center gap-2 rud-input rounded-full px-4 h-11">
                <Coins size={15} className="text-brand" />
                <input data-testid="recharge-amount" type="number" min={MIN_CREDITS} value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="bg-transparent border-none outline-none w-full text-sm" />
                <span className="text-xs text-sec">credits</span>
              </div>
            </div>

            <button data-testid="recharge-buy" onClick={buy} disabled={busy}
              className="btn-primary w-full h-11 rounded-full mt-4 flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Coins size={16} />}
              {cfg?.razorpay_enabled
                ? `Pay ₹${Math.round(Math.max(MIN_CREDITS, Math.floor(amount || 0)) * (cfg.inr_per_credit || 83.2))} · ${Math.max(MIN_CREDITS, Math.floor(amount || 0))} credits`
                : `Add ${Math.max(MIN_CREDITS, Math.floor(amount || 0))} credits (${fmtMoney(Math.max(MIN_CREDITS, Math.floor(amount || 0)))})`}
            </button>

            {BTC_ENABLED && (
              <button data-testid="recharge-btc" onClick={() => { setOpen(false); nav(`/btc?credits=${Math.max(MIN_CREDITS, Math.floor(amount || 0))}`); }}
                className="btn-ghost w-full h-10 rounded-full mt-2 flex items-center justify-center gap-2 text-sm">
                <Bitcoin size={15} /> Pay with Bitcoin / UPI instead
              </button>
            )}

            {!cfg?.razorpay_enabled && (
            <button
              data-testid="recharge-auto-topup"
              onClick={toggleAuto}
              disabled={autoBusy}
              className={`w-full mt-3 rounded-xl border px-4 py-3 flex items-center gap-3 text-left transition-colors ${autoOn ? "border-brand/60 bg-brand/10" : "border-rud hover:border-brand/40"}`}
            >
              <span className={`w-9 h-5 rounded-full shrink-0 relative transition-colors ${autoOn ? "bg-brand" : "bg-white/15"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${autoOn ? "left-[18px]" : "left-0.5"}`} />
              </span>
              <span className="text-xs leading-snug">
                <b className="text-[var(--rud-text)]">Auto-recharge</b> {Math.max(MIN_CREDITS, Math.floor(amount || 0))} credits when I run low
                <span className="block text-sec">Never get interrupted mid-search. {autoOn ? "On" : "Off"}.</span>
              </span>
            </button>
            )}

            {cfg?.razorpay_enabled && (
              <p className="text-center text-[11px] text-sec mt-3 flex items-center justify-center gap-1.5" data-testid="recharge-secure">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" /> Secure payment by Razorpay · UPI, cards & netbanking
              </p>
            )}

            {cfg?.razorpay_enabled && (
              <div className="mt-3 border-t border-rud pt-3">
                <button data-testid="recharge-qr-toggle" onClick={() => setShowQr((v) => !v)}
                  className="w-full flex items-center justify-center gap-2 text-base font-semibold text-[var(--rud-text)] hover:text-brand transition-colors">
                  <QrCode size={18} /> {showQr ? "Hide UPI QR" : "Prefer to pay by QR code?"}
                </button>
                {showQr && (
                  <div className="mt-3 rounded-xl border border-rud bg-white p-3 flex flex-col items-center" data-testid="recharge-qr">
                    <img src="/upi-qr.png" alt="ANXYA Tech UPI QR — scan & pay with any UPI app"
                      className="w-44 h-auto rounded-lg" data-testid="recharge-qr-img" />
                    <p className="text-xs text-neutral-800 text-center mt-2 leading-snug max-w-[240px]">
                      Scan with any UPI app to pay <b>ANXYA Tech Private Limited</b>. Enter the exact amount shown above
                      (₹{Math.round(Math.max(MIN_CREDITS, Math.floor(amount || 0)) * (cfg.inr_per_credit || 83.2))}).
                      <b className="text-brand"> Credits from QR payments are added manually within a few hours</b> — for instant credit, use the button above.
                    </p>
                  </div>
                )}
              </div>
            )}

            {user && (
              <button data-testid="recharge-history" onClick={() => { setOpen(false); nav("/wallet"); }}
                className="w-full mt-3 flex items-center justify-center gap-1.5 text-[11px] text-sec hover:text-brand transition-colors">
                <History size={13} /> View credits & payment history
              </button>
            )}

            {user && (
              <p className="text-center text-[11px] text-sec mt-3" data-testid="recharge-balance">
                Current balance: <b className="text-brand">{Number(user.credits || 0).toFixed(2)}</b> credits
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
